import type {
  AccessCode,
  AccessType,
  UsageLog,
} from "../api/usageLog/usageLogApiSlice";

/**
 * In-memory + localStorage-backed mock store for the generic usage_log,
 * keyed by orderId. Only used when `REACT_APP_MOCK_USAGE_LOG === "true"`
 * (off by default, prod-safe) so the config-driven check-in/check-out flow
 * can be exercised locally before the backend endpoints ship.
 */

const STORAGE_KEY = "kolula:usage-log-mock";

type MockStore = Record<string, UsageLog>;

const memoryStore: MockStore = {};

const readStorage = (): MockStore => {
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as MockStore;
  } catch {
    // localStorage may be unavailable (private mode) — fall back to memory.
    return {};
  }
};

const writeStorage = (store: MockStore): void => {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore persistence failures; the in-memory copy still works this session.
  }
};

const load = (): MockStore => {
  const persisted = readStorage();
  return { ...persisted, ...memoryStore };
};

const persist = (store: MockStore): void => {
  Object.assign(memoryStore, store);
  writeStorage(store);
};

/**
 * Mocked access type for the booking's access point (plan A3). Set
 * `REACT_APP_MOCK_ACCESS_TYPE` to `static_code` / `smart_lock` / `none` to
 * exercise the respective flow variant locally; unset keeps today's behavior
 * (field absent → hardware door step).
 */
const mockAccessType = (): AccessType | null | undefined => {
  const raw = process.env.REACT_APP_MOCK_ACCESS_TYPE;
  if (raw === "static_code" || raw === "smart_lock") return raw;
  if (raw === "none") return null;
  return undefined;
};

const defaultRecord = (orderId: string): UsageLog => ({
  orderId,
  accountId: null,
  status: "booked",
  ...(mockAccessType() !== undefined ? { accessType: mockAccessType() } : {}),
});

export const getMockUsageLog = (orderId: string): UsageLog => {
  const store = load();
  return store[orderId] ?? defaultRecord(orderId);
};

const update = (orderId: string, patch: Partial<UsageLog>): UsageLog => {
  const store = load();
  const current = store[orderId] ?? defaultRecord(orderId);
  const next: UsageLog = { ...current, ...patch, orderId };
  store[orderId] = next;
  persist(store);
  return next;
};

const nowIso = () => new Date().toISOString();

export const mockCheckIn = (
  orderId: string,
  participantCount?: number | null
): UsageLog =>
  update(orderId, {
    status: "active",
    activatedAtDate: nowIso(),
    participantCount: participantCount ?? null,
  });

export const mockFinishUsage = (
  orderId: string,
  notes?: string | null
): UsageLog =>
  update(orderId, {
    status: "finished",
    finishedAtDate: nowIso(),
    notes: notes ?? null,
  });

// The return photo is stored server-side (S3) + admin-only; the mock has nowhere
// to put an image, so it just no-ops and returns the current record. Lets the
// generic flow's check-out photo step complete locally without a backend.
export const mockSaveReturnPhoto = (orderId: string): UsageLog =>
  getMockUsageLog(orderId);

// Fixed fake code for local demos of the static-code access step. The real
// backend guards this behind the booking window / block / location checks.
export const getMockAccessCode = (): AccessCode => ({ code: "2580#" });
